// Main App component - Bus Dashboard Application

import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header/Header";
import Navigation from "./components/Navigation/Navigation";
import DataManagement from "./components/DataManagement/DataManagement";
import Dashboard from "./components/Dashboard/Dashboard";
import FuelManagement from "./components/FuelManagement/FuelManagement";
import Notification from "./components/Notification/Notification";
import useLocalStorage from "./hooks/useLocalStorage";
import useNotification from "./hooks/useNotification";
import { STORAGE_KEYS } from "./constants";
import { generateSampleData, shouldLoadSampleData } from "./utils/sampleData";
import {
  createPassengerOrigin,
  updatePassengerOrigin,
  deletePassengerOrigin,
} from "./services/api";

const App = () => {
  const [busData, setBusData] = useLocalStorage(STORAGE_KEYS.BUS_DATA, []);
  const { notification, hideNotification, showSuccess, showInfo, showError } =
    useNotification();

  // Load sample data if no data exists
  React.useEffect(() => {
    if (shouldLoadSampleData(busData)) {
      const sampleData = generateSampleData(30);
      setBusData(sampleData);
      showInfo("โหลดข้อมูลจำลอง 1 เดือนเรียบร้อยแล้ว!");
    }
  }, []);

  const handleAddRecord = async (record) => {
    try {
      const result = await createPassengerOrigin(record);

      if (result.success) {
        // Add to local state
        const newRecord = {
          ...record,
          id: result.data?.id || Date.now(),
        };
        setBusData([...busData, newRecord]);
        showSuccess("✅ บันทึกข้อมูลเรียบร้อยแล้ว!", 4000);
      } else {
        showError(result.message || "❌ เกิดข้อผิดพลาดในการบันทึกข้อมูล", 4000);
      }
    } catch (error) {
      console.error("Error adding record:", error);
      showError("❌ ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้", 4000);
    }
  };

  const handleUpdateRecord = async (id, record) => {
    try {
      const result = await updatePassengerOrigin(id, record);

      if (result.success) {
        // Update local state
        setBusData(
          busData.map((item) => (item.id === id ? { ...record, id } : item)),
        );
        showSuccess("✅ อัปเดตข้อมูลเรียบร้อยแล้ว!", 4000);
      } else {
        showError(result.message || "❌ เกิดข้อผิดพลาดในการอัปเดตข้อมูล", 4000);
      }
    } catch (error) {
      console.error("Error updating record:", error);
      showError("❌ ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้", 4000);
    }
  };

  const handleDeleteRecord = async (id) => {
    try {
      const result = await deletePassengerOrigin(id);

      if (result.success) {
        // Remove from local state
        setBusData(busData.filter((item) => item.id !== id));
        showSuccess("✅ ลบข้อมูลเรียบร้อยแล้ว!", 4000);
      } else {
        showError(result.message || "❌ เกิดข้อผิดพลาดในการลบข้อมูล", 4000);
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      showError("❌ ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้", 4000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Header />

      <main className="mx-auto px-6 py-8">
        <Navigation />

        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route path="/dashboard" element={<Dashboard data={busData} />} />

          <Route
            path="/data"
            element={
              <DataManagement
                data={busData}
                onAdd={handleAddRecord}
                onUpdate={handleUpdateRecord}
                onDelete={handleDeleteRecord}
                showNotification={showInfo}
              />
            }
          />

          <Route
            path="/fuel"
            element={<FuelManagement showNotification={showInfo} />}
          />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>

      <Notification notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default App;
